export type OnboardingRoleId =
    | "choir_member"
    | "vocalist"
    | "choir_director"
    | "vocal_coach"
    | "producer"
    | "project_owner"
    | "collaborator"
    | "producer_contributor"
    | "exploring";

export type OnboardingIntentId =
    | "practice_part"
    | "join_manage_choir"
    | "prepare_rehearsals"
    | "teach_students"
    | "discover_songs_stems"
    | "improve_vocal_skills"
    | "explore_community_challenges"
    | "work_on_projects"
    | "just_explore"
    | "practice"
    | "isolated_stems"
    | "contribute_stems"
    | "recruit_collaborators"
    | "choir_setup"
    | "explore";

export interface OnboardingRoleOption {
    id: OnboardingRoleId;
    label: string;
    description: string;
}

export interface OnboardingIntentOption {
    id: OnboardingIntentId;
    label: string;
    description: string;
}

export interface OnboardingV2Steps {
    account_created: boolean;
    role_selected: boolean;
    part_selected: boolean;
    username_confirmed: boolean;
    first_action_completed: boolean;
    dismissed_at: string | null;
    completed_at: string | null;
}

export interface OnboardingV2Profile {
    role: OnboardingRoleId | null;
    intent: OnboardingIntentId | null;
    intents: OnboardingIntentId[];
    default_part: string | null;
    generated_username: boolean;
    username_confirmed: boolean;
}

export interface OnboardingV2Preferences {
    version: number;
    dismissed: boolean;
    completed: boolean;
    recommended_route: string | null;
    recommended_track: string | null;
    steps: OnboardingV2Steps;
    profile: OnboardingV2Profile;
    actions: OnboardingV2Actions;
}

export type OnboardingActionId =
    | "choir_joined"
    | "first_stem_played"
    | "multitrack_opened"
    | "first_practice_started"
    | "first_practice_submitted"
    | "first_exercise_game_opened"
    | "choir_created"
    | "setlist_created"
    | "rehearsal_created"
    | "first_member_invited"
    | "first_contribution_started"
    | "first_contribution_uploaded"
    | "first_project_interest_expressed"
    | "project_created"
    | "project_application_submitted"
    | "first_project_invite_sent"
    | "challenge_created"
    | "challenge_interest_expressed"
    | "challenge_entry_submitted"
    | "teaching_workspace_created"
    | "training_course_created"
    | "training_cohort_created"
    | "first_training_assignment_published"
    | "first_student_invited"
    | "route_entry_fallback";

export interface OnboardingActionEntry {
    id: OnboardingActionId;
    at: string;
    source: "analytics" | "onboarding" | "cta" | "fallback";
}

export interface OnboardingV2Actions {
    completed: OnboardingActionId[];
    timeline: OnboardingActionEntry[];
    last_action_at: string | null;
    activated_at: string | null;
}

export type DeepPartial<T> = {
    [K in keyof T]?: T[K] extends Array<infer U>
        ? Array<U>
        : T[K] extends object
            ? DeepPartial<T[K]>
            : T[K];
};

export interface OnboardingRecommendation {
    route: string;
    track: string;
    title: string;
    description: string;
    ctaLabel: string;
}

const DEFAULT_SIGNUP_LAST_NAME = "user";
const DEFAULT_SIGNUP_FIRST_NAMES = new Set(["new", "vocalcentric"]);

const ONBOARDING_INTENT_ID_SET = new Set<OnboardingIntentId>([
    "practice_part",
    "join_manage_choir",
    "prepare_rehearsals",
    "teach_students",
    "discover_songs_stems",
    "improve_vocal_skills",
    "explore_community_challenges",
    "work_on_projects",
    "just_explore",
    "practice",
    "isolated_stems",
    "contribute_stems",
    "recruit_collaborators",
    "choir_setup",
    "explore",
]);

export const ONBOARDING_V2_ALLOWED_ROUTE_PREFIXES = [
    "/home",
    "/rehearsals",
    "/choirs",
    "/teaching",
    "/projects",
    "/community",
    "/contributions",
    "/account-information",
    "/uploads",
] as const;

export const ONBOARDING_ROLE_OPTIONS: OnboardingRoleOption[] = [
    {
        id: "choir_member",
        label: "Choir Member",
        description: "I mostly rehearse and perform as a choir participant.",
    },
    {
        id: "vocalist",
        label: "Solo Vocalist / Artist",
        description: "I want tools to strengthen my personal performance workflow.",
    },
    {
        id: "choir_director",
        label: "Choir Director/Admin",
        description: "I want to organize rehearsals, people, and choir flow.",
    },
    {
        id: "vocal_coach",
        label: "Vocal Coach / Teacher",
        description: "I teach students, organize curriculum, and run training cohorts.",
    },
    {
        id: "producer",
        label: "Producer",
        description: "I contribute songs and stems to the marketplace.",
    },
    {
        id: "project_owner",
        label: "Project Owner",
        description: "I create projects and invite others to collaborate.",
    },
    {
        id: "collaborator",
        label: "Collaborator",
        description: "I explore gigs/projects and keep my profile ready for opportunities.",
    },
    {
        id: "exploring",
        label: "Just Exploring",
        description: "I want the fastest guided path and can refine later.",
    },
];

export const ONBOARDING_INTENT_OPTIONS: Record<OnboardingRoleId, OnboardingIntentOption[]> = {
    choir_member: [
        {
            id: "practice_part",
            label: "Practice my part",
            description: "Use stems and focused playback to lock in your section.",
        },
        {
            id: "join_manage_choir",
            label: "Join or manage a choir",
            description: "Work with members, parts, and choir activities in one place.",
        },
        {
            id: "prepare_rehearsals",
            label: "Prepare rehearsals",
            description: "Get rehearsal-ready with setlists, schedules, and song organization.",
        },
        {
            id: "discover_songs_stems",
            label: "Discover songs and stems",
            description: "Browse songs, artists, and musical resources for rehearsal.",
        },
        {
            id: "improve_vocal_skills",
            label: "Improve my vocal skills",
            description: "Build consistency with structured practice and performance focus.",
        },
        {
            id: "explore_community_challenges",
            label: "Explore community and challenges",
            description: "Join contributions, reels, and challenge-driven engagement.",
        },
        {
            id: "work_on_projects",
            label: "Work on projects",
            description: "Find collaboration opportunities and manage project workflows.",
        },
        {
            id: "just_explore",
            label: "Just explore",
            description: "I want to look around and discover what fits best.",
        },
    ],
    vocalist: [
        {
            id: "practice_part",
            label: "Practice my part",
            description: "Use stems and focused playback to lock in your section.",
        },
        {
            id: "discover_songs_stems",
            label: "Discover songs and stems",
            description: "Browse songs, artists, and musical resources for rehearsal.",
        },
        {
            id: "improve_vocal_skills",
            label: "Improve my vocal skills",
            description: "Build consistency with structured practice and performance focus.",
        },
        {
            id: "explore_community_challenges",
            label: "Explore community and challenges",
            description: "Join contributions, reels, and challenge-driven engagement.",
        },
        {
            id: "just_explore",
            label: "Just explore",
            description: "I want to look around and discover what fits best.",
        },
    ],
    choir_director: [
        {
            id: "join_manage_choir",
            label: "Join or manage a choir",
            description: "Work with members, parts, and choir activities in one place.",
        },
        {
            id: "prepare_rehearsals",
            label: "Prepare rehearsals",
            description: "Get rehearsal-ready with setlists, schedules, and song organization.",
        },
        {
            id: "work_on_projects",
            label: "Work on projects",
            description: "Find collaboration opportunities and manage project workflows.",
        },
        {
            id: "just_explore",
            label: "Just explore",
            description: "I want to look around and discover what fits best.",
        },
    ],
    vocal_coach: [
        {
            id: "teach_students",
            label: "Teach students",
            description: "Create courses, organize cohorts, and assign structured vocal training.",
        },
        {
            id: "improve_vocal_skills",
            label: "Build vocal skills",
            description: "Use structured exercises to strengthen student technique.",
        },
        {
            id: "just_explore",
            label: "Just explore",
            description: "Look around before setting up a teaching workspace.",
        },
    ],
    producer: [
        {
            id: "contribute_stems",
            label: "Contribute songs and stems",
            description: "Upload marketplace-ready content and maintain your catalog.",
        },
        {
            id: "discover_songs_stems",
            label: "Discover songs and stems",
            description: "Browse songs, artists, and musical resources for rehearsal.",
        },
        {
            id: "just_explore",
            label: "Just explore",
            description: "I want to look around and discover what fits best.",
        },
    ],
    project_owner: [
        {
            id: "work_on_projects",
            label: "Work on projects",
            description: "Create projects and manage collaboration workflows.",
        },
        {
            id: "recruit_collaborators",
            label: "Recruit collaborators",
            description: "Invite qualified contributors to active opportunities.",
        },
        {
            id: "just_explore",
            label: "Just explore",
            description: "I want to look around and discover what fits best.",
        },
    ],
    collaborator: [
        {
            id: "work_on_projects",
            label: "Find projects and gigs",
            description: "Explore opportunities and apply where your profile matches.",
        },
        {
            id: "recruit_collaborators",
            label: "Build collaboration network",
            description: "Connect with project owners and contributors for opportunities.",
        },
        {
            id: "just_explore",
            label: "Just explore",
            description: "I want to look around and discover what fits best.",
        },
    ],
    producer_contributor: [
        {
            id: "work_on_projects",
            label: "Work on projects",
            description: "Find collaboration opportunities and manage project workflows.",
        },
        {
            id: "explore_community_challenges",
            label: "Explore community and challenges",
            description: "Join contributions, reels, and challenge-driven engagement.",
        },
        {
            id: "discover_songs_stems",
            label: "Discover songs and stems",
            description: "Browse songs, artists, and musical resources for rehearsal.",
        },
        {
            id: "just_explore",
            label: "Just explore",
            description: "I want to look around and discover what fits best.",
        },
    ],
    exploring: [
        {
            id: "practice_part",
            label: "Practice my part",
            description: "Use stems and focused playback to lock in your section.",
        },
        {
            id: "discover_songs_stems",
            label: "Discover songs and stems",
            description: "Browse songs, artists, and musical resources for rehearsal.",
        },
        {
            id: "explore_community_challenges",
            label: "Explore community and challenges",
            description: "Join contributions, reels, and challenge-driven engagement.",
        },
        {
            id: "just_explore",
            label: "Just explore",
            description: "I want to look around and discover what fits best.",
        },
    ],
};

export const ONBOARDING_ALL_INTENT_OPTIONS: OnboardingIntentOption[] = ONBOARDING_INTENT_OPTIONS.choir_member;

export function inferRoleFromIntents(intents: OnboardingIntentId[]): OnboardingRoleId | null {
    if (!intents.length) return null;
    if (intents.includes("teach_students")) {
        return "vocal_coach";
    }
    if (intents.includes("join_manage_choir") || intents.includes("prepare_rehearsals")) {
        return "choir_director";
    }
    if (intents.includes("work_on_projects")) {
        return "project_owner";
    }
    if (intents.includes("practice_part") || intents.includes("improve_vocal_skills")) {
        return "choir_member";
    }
    if (intents.includes("discover_songs_stems")) {
        return "vocalist";
    }
    if (intents.includes("just_explore") || intents.includes("explore")) {
        return "exploring";
    }
    return null;
}

function isVocalRole(role: OnboardingRoleId | null | undefined): boolean {
    return role === "vocalist" || role === "choir_member";
}

function isPartRequired(role: OnboardingRoleId | null | undefined): boolean {
    return role !== "vocal_coach";
}

function normalizeProfileName(value: string | null | undefined): string {
    return String(value || "").trim().toLowerCase();
}

export function isDefaultOnboardingName(
    firstName: string | null | undefined,
    lastName: string | null | undefined
): boolean {
    return DEFAULT_SIGNUP_FIRST_NAMES.has(normalizeProfileName(firstName))
        && normalizeProfileName(lastName) === DEFAULT_SIGNUP_LAST_NAME;
}

export function hasCompleteOnboardingName(
    firstName: string | null | undefined,
    lastName: string | null | undefined
): boolean {
    return Boolean(String(firstName || "").trim())
        && Boolean(String(lastName || "").trim())
        && !isDefaultOnboardingName(firstName, lastName);
}

export function hasCompleteOnboardingUsername(
    username: string | null | undefined,
    hasSetUsername: boolean | null | undefined
): boolean {
    return Boolean(hasSetUsername)
        && /^[a-zA-Z0-9_]{3,30}$/.test(String(username || "").trim());
}

export function createDefaultOnboardingV2Preferences(
    seed: DeepPartial<OnboardingV2Preferences> = {}
): OnboardingV2Preferences {
    const seedSteps = seed.steps || {};
    const seedProfile = seed.profile || {};
    const seedActions = seed.actions || {};
    const rawIntents = Array.isArray(seedProfile.intents)
        ? seedProfile.intents
        : (seedProfile.intent ? [seedProfile.intent] : []);
    const normalizedIntents = rawIntents.filter((intent): intent is OnboardingIntentId => ONBOARDING_INTENT_ID_SET.has(intent as OnboardingIntentId));

    return {
        version: Number.isFinite(Number(seed.version)) ? Number(seed.version) : 2,
        dismissed: typeof seed.dismissed === "boolean" ? seed.dismissed : false,
        completed: typeof seed.completed === "boolean" ? seed.completed : false,
        recommended_route: seed.recommended_route ?? null,
        recommended_track: seed.recommended_track ?? null,
        steps: {
            account_created: typeof seedSteps.account_created === "boolean" ? seedSteps.account_created : true,
            role_selected: typeof seedSteps.role_selected === "boolean" ? seedSteps.role_selected : false,
            part_selected: typeof seedSteps.part_selected === "boolean" ? seedSteps.part_selected : false,
            username_confirmed: typeof seedSteps.username_confirmed === "boolean" ? seedSteps.username_confirmed : false,
            first_action_completed: typeof seedSteps.first_action_completed === "boolean" ? seedSteps.first_action_completed : false,
            dismissed_at: seedSteps.dismissed_at ?? null,
            completed_at: seedSteps.completed_at ?? null,
        },
        profile: {
            role: seedProfile.role ?? null,
            intent: seedProfile.intent ?? null,
            intents: normalizedIntents,
            default_part: seedProfile.default_part ?? null,
            generated_username: typeof seedProfile.generated_username === "boolean" ? seedProfile.generated_username : false,
            username_confirmed: typeof seedProfile.username_confirmed === "boolean" ? seedProfile.username_confirmed : false,
        },
        actions: {
            completed: Array.isArray(seedActions.completed) ? seedActions.completed : [],
            timeline: Array.isArray(seedActions.timeline) ? seedActions.timeline : [],
            last_action_at: seedActions.last_action_at ?? null,
            activated_at: seedActions.activated_at ?? null,
        },
    };
}

export function validateOnboardingRoute(route: string | null | undefined): string {
    if (!route || typeof route !== "string") {
        return "/home";
    }
    const valid = ONBOARDING_V2_ALLOWED_ROUTE_PREFIXES.some((prefix) => route === prefix || route.startsWith(`${prefix}/`));
    return valid ? route : "/home";
}

export function getOnboardingRecommendation(
    role: OnboardingRoleId | null,
    intent: OnboardingIntentId | null,
    defaultPart: string | null,
    intents: OnboardingIntentId[] = []
): OnboardingRecommendation {
    const resolvedIntent = intent || intents[0] || null;

    if (role === "vocal_coach" || resolvedIntent === "teach_students") {
        return {
            route: validateOnboardingRoute("/teaching"),
            track: "coach_first_workspace",
            title: "Set up your teaching workspace",
            description: "Create your workspace, organize a course, and invite your first student.",
            ctaLabel: "Open Teaching",
        };
    }

    if (role === "choir_member" && resolvedIntent === "join_manage_choir") {
        return {
            route: validateOnboardingRoute("/choirs/choirs-you-can-join"),
            track: "choir_member_join_choir",
            title: "Find a choir to join",
            description: "Browse active choirs and send your first join request from the list.",
            ctaLabel: "Explore choirs",
        };
    }

    if (
        role === "choir_director"
        || resolvedIntent === "prepare_rehearsals"
        || resolvedIntent === "choir_setup"
        || (resolvedIntent === "join_manage_choir" && role !== "choir_member")
    ) {
        return {
            route: validateOnboardingRoute("/choirs/manged-by-you"),
            track: "director_first_choir",
            title: "Open your choir workspace",
            description: "Start from the choir management area, then create your choir when you are ready.",
            ctaLabel: "Open choirs",
        };
    }

    if (role === "producer" || resolvedIntent === "contribute_stems") {
        return {
            route: validateOnboardingRoute("/account-information/uploads"),
            track: "producer_upload_song",
            title: "Upload your first song",
            description: "Upload flows are available for whitelisted accounts. Continue to account uploads to get started.",
            ctaLabel: "Open uploads",
        };
    }

    if (role === "project_owner") {
        return {
            route: validateOnboardingRoute("/projects/create"),
            track: "project_owner_first_project",
            title: "Create your first project",
            description: "Create a project first, then invite collaborators to start active work.",
            ctaLabel: "Create project",
        };
    }

    if (role === "collaborator" || role === "producer_contributor") {
        return {
            route: validateOnboardingRoute("/projects"),
            track: "collaborator_discover_projects",
            title: "Explore active projects",
            description: "Discover open gigs and collaboration opportunities that match your profile.",
            ctaLabel: "Open projects",
        };
    }

    if (resolvedIntent === "work_on_projects" || resolvedIntent === "recruit_collaborators") {
        return {
            route: validateOnboardingRoute("/projects"),
            track: "projects_first_step",
            title: "Start your first project flow",
            description: "Open projects to discover active collaboration opportunities quickly.",
            ctaLabel: "Open projects",
        };
    }

    if (resolvedIntent === "explore_community_challenges") {
        return {
            route: validateOnboardingRoute("/community/challenges"),
            track: "community_first_step",
            title: "Explore community challenges",
            description: "Join one challenge to see how VocalCentric drives consistent engagement.",
            ctaLabel: "Open challenges",
        };
    }

    if (
        resolvedIntent === "discover_songs_stems"
        || resolvedIntent === "isolated_stems"
        || resolvedIntent === "practice"
        || resolvedIntent === "practice_part"
        || resolvedIntent === "improve_vocal_skills"
    ) {
        return {
            route: validateOnboardingRoute("/home"),
            track: defaultPart ? `practice_${defaultPart}` : "practice_general",
            title: "Hear your part in isolation",
            description: "Start with your selected part so VocalCentric can guide your practice more accurately.",
            ctaLabel: "Play my part",
        };
    }

    return {
        route: validateOnboardingRoute("/home"),
        track: "explore_first_practice",
        title: "Start exploring VocalCentric",
        description: "Browse key modules and take one action to discover the best path for you.",
        ctaLabel: "Explore now",
    };
}

function getActivationActionCandidates(preferences: OnboardingV2Preferences): OnboardingActionId[] {
    const role = preferences.profile.role;
    const intent = preferences.profile.intent || preferences.profile.intents?.[0];
    const vocalActions: OnboardingActionId[] = [
        "first_stem_played",
        "multitrack_opened",
        "first_practice_started",
        "first_practice_submitted",
        "first_exercise_game_opened",
    ];

    if (role === "choir_member") {
        return ["choir_joined", ...vocalActions];
    }
    if (role === "vocalist") {
        return ["choir_joined", ...vocalActions];
    }
    if (role === "choir_director") {
        return ["choir_joined", "choir_created", "setlist_created", "rehearsal_created", "first_member_invited"];
    }
    if (role === "vocal_coach") {
        return ["teaching_workspace_created", "training_course_created", "training_cohort_created", "first_training_assignment_published", "first_student_invited"];
    }
    if (role === "producer") {
        return ["first_contribution_uploaded", "first_contribution_started"];
    }
    if (role === "project_owner" || role === "collaborator" || role === "producer_contributor") {
        if (role === "project_owner") {
            return ["project_created", "first_project_invite_sent", "first_project_interest_expressed"];
        }
        if (role === "collaborator") {
            return ["project_application_submitted", "first_project_interest_expressed"];
        }
        return ["first_contribution_started", "first_contribution_uploaded", "project_application_submitted", "first_project_interest_expressed"];
    }
    if (intent === "recruit_collaborators") {
        return ["project_created", "first_project_invite_sent", "first_project_interest_expressed", "first_member_invited"];
    }
    if (intent === "join_manage_choir") {
        return ["choir_joined", "choir_created", "setlist_created", "rehearsal_created", "first_member_invited"];
    }
    if (intent === "prepare_rehearsals" || intent === "choir_setup") {
        return ["choir_created", "setlist_created", "rehearsal_created", "first_member_invited"];
    }
    if (intent === "teach_students") {
        return ["teaching_workspace_created", "training_course_created", "training_cohort_created", "first_training_assignment_published", "first_student_invited"];
    }
    if (intent === "contribute_stems") {
        return ["first_contribution_started", "first_contribution_uploaded"];
    }
    if (intent === "explore_community_challenges") {
        return ["challenge_entry_submitted", "challenge_interest_expressed", "challenge_created"];
    }
    return ["choir_joined", ...vocalActions, "challenge_entry_submitted", "first_project_interest_expressed"];
}

export function isActivationReached(preferences: OnboardingV2Preferences): boolean {
    const completed = new Set(preferences.actions.completed || []);
    return getActivationActionCandidates(preferences).some((action) => completed.has(action));
}

export function getOnboardingProgressSummary(preferences: OnboardingV2Preferences): {
    completed: number;
    total: number;
} {
    const roleSelected = preferences.steps.role_selected || Boolean(preferences.profile.role);
    const partSelected = !isPartRequired(preferences.profile.role) || preferences.steps.part_selected || Boolean(preferences.profile.default_part);
    const usernameConfirmed = preferences.steps.username_confirmed || Boolean(preferences.profile.username_confirmed);
    const checkpoints = [
        preferences.steps.account_created,
        roleSelected,
        usernameConfirmed,
        isActivationReached(preferences),
    ];

    if (isPartRequired(preferences.profile.role)) checkpoints.push(partSelected);

    return {
        completed: checkpoints.filter(Boolean).length,
        total: checkpoints.length,
    };
}

export function isOnboardingV2Complete(preferences: OnboardingV2Preferences): boolean {
    const activationReached = isActivationReached(preferences);
    const roleSelected = preferences.steps.role_selected || Boolean(preferences.profile.role);
    const partSelected = !isPartRequired(preferences.profile.role) || preferences.steps.part_selected || Boolean(preferences.profile.default_part);
    const usernameConfirmed = preferences.steps.username_confirmed || Boolean(preferences.profile.username_confirmed);
    const baseComplete =
        preferences.steps.account_created
        && roleSelected
        && usernameConfirmed
        && activationReached;

    return baseComplete && partSelected;
}

export function getOnboardingNextAction(preferences: OnboardingV2Preferences): string {
    const roleSelected = preferences.steps.role_selected || Boolean(preferences.profile.role);
    const partSelected = !isPartRequired(preferences.profile.role) || preferences.steps.part_selected || Boolean(preferences.profile.default_part);
    const usernameConfirmed = preferences.steps.username_confirmed || Boolean(preferences.profile.username_confirmed);
    if (!roleSelected) return "select_role";
    if (!partSelected) return "select_part";
    if (!usernameConfirmed) return "confirm_username";
    if (!isActivationReached(preferences)) return "take_first_action";
    return "completed";
}

export function applyOnboardingAction(
    preferences: OnboardingV2Preferences,
    actionId: OnboardingActionId,
    source: OnboardingActionEntry["source"] = "analytics"
): OnboardingV2Preferences {
    const now = new Date().toISOString();
    const completedSet = new Set(preferences.actions.completed || []);
    completedSet.add(actionId);

    const next = createDefaultOnboardingV2Preferences({
        ...preferences,
        actions: {
            ...preferences.actions,
            completed: Array.from(completedSet),
            timeline: [
                ...(preferences.actions.timeline || []).slice(-29),
                {id: actionId, at: now, source},
            ],
            last_action_at: now,
        },
        steps: {
            ...preferences.steps,
            first_action_completed: true,
        },
    });

    if (isActivationReached(next)) {
        next.actions.activated_at = next.actions.activated_at || now;
        next.steps.first_action_completed = true;
        next.steps.completed_at = next.steps.completed_at || now;
        next.completed = isOnboardingV2Complete(next);
    }

    return next;
}
